import type { IOK } from '@ok/interfaces/ok/IOK';
import { getInstance as getCurrentUserCfg } from 'OK/CurrentUserCfg';
import {
    ILoggerConfig,
    clob,
    duration,
    error,
    force,
    raw,
    sendLog,
    success,
    trace,
    updateLoggerConfig
} from 'OK/LoggerCore';

declare var OK: IOK;

const Tracer = OK.Tracer;

let logSendPeriod = 15000;
let logSendTimeoutId: number | undefined;

function onLogSendTimeout() {
    sendLog();
    scheduleLogSend();
}

function cancelLogSend() {
    clearTimeout(logSendTimeoutId);
}

function scheduleLogSend() {
    cancelLogSend();
    logSendTimeoutId = setTimeout(onLogSendTimeout, logSendPeriod);
}

let beforeUnloadListenerAdded = false;

function onBeforeUnload() {
    sendLog();
    updateLoggerConfig({immediateSending: true});
    cancelLogSend();
}

function listenBeforeUnload(stopListen?: boolean) {
    if (!stopListen && !beforeUnloadListenerAdded) {
        window.addEventListener('beforeunload', onBeforeUnload);
        beforeUnloadListenerAdded = true;
    } else if (stopListen && beforeUnloadListenerAdded) {
        window.removeEventListener('beforeunload', onBeforeUnload);
        beforeUnloadListenerAdded = false;
    }
}

let visibilityChangeListenerAdded = false;

function onVisibilityChange() {
    // Отправляем логи и перезапускаем таймер
    onLogSendTimeout();
}

function listenVisibilityChange(stopListen?: boolean) {
    if (!stopListen && !visibilityChangeListenerAdded) {
        document.addEventListener('visibilitychange', onVisibilityChange);
        visibilityChangeListenerAdded = true;
    } else if (stopListen && visibilityChangeListenerAdded) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        visibilityChangeListenerAdded = false;
    }
}

type IConfigData = Record<string, unknown>;
type IConfigValue = IConfigData | undefined;

function updateConfigFromData(configData: IConfigData) {
    const loggerConfig: Partial<ILoggerConfig> = {};
    // См. one.app.community.dk.blocks.user.conf.GwtLoggerConfigRBlock.BlockCtx.getState
    if ('isGwtLogClobEnabled' in configData) {
        loggerConfig.clobLogEnabled = Boolean(configData.isGwtLogClobEnabled);
    }
    if (configData.gwtClobOperations && Array.isArray(configData.gwtClobOperations)) {
        loggerConfig.clobOperations = configData.gwtClobOperations;
    }
    if (configData.gwtLogOperationsDisabled && Array.isArray(configData.gwtLogOperationsDisabled)) {
        loggerConfig.disabledLogOperations = configData.gwtLogOperationsDisabled;
    }
    if ('isGwtLogEnabled' in configData) {
        loggerConfig.enabled = Boolean(configData.isGwtLogEnabled);
    }
    if (configData.gUO && Array.isArray(configData.gUO)) {
        loggerConfig.filterByTimeOperations = configData.gUO;
    }
    if (configData.gwtLogOperations && Array.isArray(configData.gwtLogOperations)) {
        loggerConfig.logOperations = configData.gwtLogOperations;
    }
    if (configData.gwtRawOperations && Array.isArray(configData.gwtRawOperations)) {
        loggerConfig.rawOperations = configData.gwtRawOperations;
    }
    updateLoggerConfig(loggerConfig);

    // См. one.app.community.dk.conf.GwtLoggerConfiguration.getGwtLogAggregatePeriod
    if (typeof configData.gwtLogAggregatePeriod === 'number' && configData.gwtLogAggregatePeriod > 0) {
        logSendPeriod = configData.gwtLogAggregatePeriod;
    }
}

function updateConfigFromElement(configElement: HTMLElement | null): IConfigValue {
    if (!configElement || !configElement.firstChild) {
        if (!configElement) {
            Tracer.error(new Error('No config element to update logger config'));
        } else {
            Tracer.error(new Error('Config element to update logger config is empty'), {element: configElement.outerHTML});
        }
        return;
    }

    let configStr = configElement.firstChild.textContent;
    if (!configStr) {
        Tracer.error(new Error('No configuration is present in config element to update logger config'), {element: configElement.outerHTML});
        return;
    }
    let configData: IConfigValue;
    let errorSent = false;
    try {
        configData = JSON.parse(configStr);
    } catch (err) {
        let location = 'LoggerCoreHook';
        if (configElement.id) {
            location += '/' + configElement.id;
        }
        error(location, 'configParse');
        Tracer.error(err as Error, {element: configElement.outerHTML});
        errorSent = true;
    }
    if (!configData || typeof configData !== 'object') {
        if (!errorSent) {
            Tracer.error(new Error('Incorrect config value to update logger config'), {element: configElement.outerHTML});
        }
        return;
    }

    updateConfigFromData(configData);

    return configData;
}

function defineLogger() {
    const { logger } = OK;
    logger.clob = clob;
    logger.duration = duration;
    logger.error = error;
    logger.force = force;
    logger.raw = raw;
    logger.success = success;
    logger.trace = trace;
}

let loggerActivated = false;

export function activate(element: HTMLElement) {
    if (loggerActivated) {
        return;
    }

    const configId = element.getAttribute('data-config-id');
    let configData: IConfigValue;
    if (configId) {
        configData = updateConfigFromElement(document.getElementById(configId));
    } else {
        Tracer.error(new Error('No config id found to initialize logger'), {element: element.outerHTML});
    }

    defineLogger();

    // Записываем накопленные логи
    OK.logger.replayQueue();

    scheduleLogSend();
    if (configData) {
        // См. one.app.community.dk.conf.GwtLoggerConfiguration.isSendLogOnShowHideEnabled
        if (configData.sendOnShowHide) {
            listenVisibilityChange();
        }
        // См. one.app.community.dk.conf.GwtLoggerConfiguration.isOnBeforeUnloadEnabled
        if (configData.onBeforeUnloadEnabled) {
            listenBeforeUnload();
        }
    }

    const stateId = (window.OK.getCurrentDesktopModelId && window.OK.getCurrentDesktopModelId()) || '_noData_';
    success(
        'logInit',
        `core|${stateId}`,
        getCurrentUserCfg().getUserId()
            ? 'user'
            : 'anonym'
    );
    loggerActivated = true;
}

export function deactivate() {
    cancelLogSend();
    listenVisibilityChange(true);
    listenBeforeUnload(true);
    loggerActivated = false;
}
